import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, nextTick, onMounted } from 'vue'
import store from '@/store/store'
import { useI18n } from 'vue-i18n-composable'
import { ValidationObserver } from 'vee-validate'
import { ContextName, ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import { ConfigurationAlias } from '@/store/modules/configuration/configurationTypes'
import { ActionEntityId, ExpedientEntityId } from '@/general/entityIds'
import CustomDialogActionButtonsComponent from '@/components/Dialog/CustomDialog/CustomDialogActionButtonsComponent.vue'
import CustomDialogHeaderComponent from '@/components/Dialog/CustomDialog/CustomDialogHeaderComponent.vue'
import LexonAutocompleteComponent from '@/components/forms/fields/LexonAutocomplete/LexonAutocompleteComponent.vue'
import LexonTextFieldComponent from '@/components/forms/fields/LexonTextField/LexonTextFieldComponent.vue'
import LexonTextAreaComponent from '@/components/forms/fields/LexonTextArea/LexonTextAreaComponent.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SendNotificationMailInternComponent',
  props: {
    componentProps: { type: Object, required: true }
  },
  setup(__props: any) {

const props = __props as {
  componentProps: { context: string; source: string }
};

const { t } = useI18n()



const dialogModule: string = ModuleNamespaces.DIALOG
const selectedRegisterModule: string = ModuleNamespaces.SELECTED_REGISTER
const usersModule = ModuleNamespaces.USERS
const notificationModule: string = ModuleNamespaces.NOTIFICATIONS
const spinnerModule: string = ModuleNamespaces.SPINNER

const nameInput = ref<InstanceType<typeof ValidationObserver>>()

const subject = ref('')
const responsibles = ref<any[]>([])
const selectedResponsibles = ref<any[]>([])
const maxNumberOfCharactersInNotesField = 2000
const noteText = ref('')

const texts = {
  allSections: t('action_buttons.select_all'),
  destLabel: t('components.notifications.dest_label'),
  noteLabel: t('components.notifications.note_label'),
  messageLabel: t('components.notifications.message_label'),
  notePlaceholder: t('components.notifications.note_placeholder'),
  primaryButton: t('components.notifications.primary_button'),
  requiredFields: t('components.form_generator.required_fields'),
  subjectLabel: t('components.notifications.subject_label'),
  title: t('components.notifications.title'),
  responsiblePlaceholder: t('components.notifications.responsible_placeholder'),
  subjectPlaceholder: t('components.notifications.subject_placeholder')
}

const typeIdToTranslation: Record<string, string> = {
  LLAM: t('routes.actions_calls') as string,
  EMAIL: t('routes.actions_emails') as string,
  GEST: t('routes.actions_internal_management') as string,
  LEXN: t('routes.actions_lexnet') as string,
  REUN: t('routes.actions_meetings') as string,
  OTRA: t('routes.actions_others') as string,
  TRAM: t('routes.actions_procedures') as string,
  TARE: t('routes.actions_tasks') as string
}

const disabledActionButton = computed(() => {
  const hasResponsibles = selectedResponsibles.value.length > 0
  const hasSubject = (subject.value?.trim() ?? '') !== '' && (subject.value?.length ?? 0) <= 255
  const isNoteWithinLimit = (noteText.value?.length ?? 0) <= maxNumberOfCharactersInNotesField
  const hasNote = (noteText.value?.trim() ?? '') !== '' && isNoteWithinLimit

  return !hasResponsibles || !hasSubject || !hasNote
})

const selectedRegisterData = (context: string) =>
  store.getters[`${selectedRegisterModule}/getSelectedRegisterData`](context)

const closeCustomDialog = () => store.dispatch(`${dialogModule}/closeCustomDialog`)

const fetchFilteredResponsibles = (params: object) => store.dispatch(`${usersModule}/fetchFilteredResponsibles`, params)

const sendMailNotification = (payload: any) => store.dispatch(`${notificationModule}/sendMailNotification`, payload)
const saveMessage = (notificationData: any) => store.dispatch(`${notificationModule}/saveMessage`, notificationData)

const showSpinner = () => store.dispatch(`${spinnerModule}/showSpinner`)
const hideSpinner = () => store.dispatch(`${spinnerModule}/hideSpinner`)

async function validateSubjectField() {
  await nextTick()
  nameInput.value?.validate()
}

function parseResponsibles() {
  const { idUser, userId } = selectedRegisterData(props.componentProps.context) || {}
  const userArr = Array.isArray(idUser) ? idUser : []
  if (userArr.length === 0) {
    userArr.push(userId.toString())
  }
  responsibles.value.forEach((resp: any) => {
    if (userArr.includes(resp.id)) {
      selectedResponsibles.value.push(resp)
    }
  })
}

async function sendMail() {
  showSpinner()
  const {
    idCustomer,
    idExpedient,
    idOwnFile,
    subject: actionSubject,
    actionId,
    typeId
  } = selectedRegisterData(props.componentProps.context)

  trackEvent(TrackerEvents.SEND_INTERNAL_MESSAGE, {
    origin: props.componentProps.context,
    total_recipients: selectedResponsibles.value.length,
    total_characters: noteText.value.length
  })
  const notificationData = {
    actionId,
    actionSubject,
    actionType: getTranslatedType(typeId),
    idExpedient,
    idCustomer,
    refExpedient: idOwnFile,
    selectedResponsibles: filterResponsibibles(selectedResponsibles.value),
    subject: subject.value,
    noteText: noteText.value
  }

  try {
    const sended = await sendMailNotification({
      templateType: getTemplateTypeBySource(),
      notificationData
    })
    if (sended) {
      await saveMessage({ notificationData })
      const registerId = props.componentProps.context === ContextName.EXPEDIENTS ? idExpedient : actionId
      await store.dispatch(`${notificationModule}/fetchContextInternalMessages`, {
        context: props.componentProps.context,
        registerId
      })
    }
  } catch (_error) {
  } finally {
    closeCustomDialog()
    hideSpinner()
  }
}

function filterResponsibibles(resp: any) {
  const respFiltered: any = []
  resp.forEach((element: any) => {
    respFiltered.push({ name: element.name, email: element.email, id: element.id })
  })
  return respFiltered
}

function getTemplateTypeBySource() {
  switch (props.componentProps.source) {
    case ConfigurationAlias.ACTION:
      return noteText.value === '' ? 'internalAction' : 'internalActionNote'
    case ConfigurationAlias.EXPEDIENT:
      return noteText.value === '' ? 'internal' : 'internalNote'
    default:
      return ''
  }
}

function getTranslatedType(typeId: string) {
  if (typeId in typeIdToTranslation) {
    return typeIdToTranslation[typeId as string]
  }
  return typeId
}

onMounted(async () => {
  const { idExpedient, actionId } = selectedRegisterData(props.componentProps.context)
  const params = {
    idEntityType: idExpedient ? ExpedientEntityId.EXPEDIENTS : ActionEntityId.ALL,
    idEntity: idExpedient || actionId
  }
  responsibles.value = await fetchFilteredResponsibles(params)
  await parseResponsibles()
  validateSubjectField()
})

return { __sfc: true,t, props, dialogModule, selectedRegisterModule, usersModule, notificationModule, spinnerModule, nameInput, subject, responsibles, selectedResponsibles, maxNumberOfCharactersInNotesField, noteText, texts, typeIdToTranslation, disabledActionButton, selectedRegisterData, closeCustomDialog, fetchFilteredResponsibles, sendMailNotification, saveMessage, showSpinner, hideSpinner, validateSubjectField, parseResponsibles, sendMail, filterResponsibibles, getTemplateTypeBySource, getTranslatedType, ValidationObserver, CustomDialogActionButtonsComponent, CustomDialogHeaderComponent, LexonAutocompleteComponent, LexonTextFieldComponent, LexonTextAreaComponent }
}

})